import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface Organization {
  id: number;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  userCount?: number;
}

export interface OrganizationDetail extends Organization {
  users?: OrganizationUser[];
}

export interface OrganizationUser {
  id: string;
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roleId: number;
  assignedAt: string;
}

export interface ActiveUser {
  id: string;
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roleId: number;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateOrganizationDTO {
  name: string;
  isActive?: boolean;
}

export interface UpdateOrganizationDTO {
  name?: string;
  isActive?: boolean;
}

// ===== API RESPONSE INTERFACES =====

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: Pagination;
}

// ===== SERVICE FUNCTIONS =====

/**
 * Get all organizations with filtering and pagination
 */
export const getAllOrganizations = async (
  params: {
    search?: string;
    isActive?: boolean | string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{
  success: boolean;
  data?: Organization[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.isActive !== undefined && params.isActive !== '') {
      queryParams.append('isActive', String(params.isActive));
    }
    queryParams.append('page', String(params.page || 1));
    queryParams.append('limit', String(params.limit || 10));

    const response = await httpClient.get<ApiResponse<Organization[]>>(
      `/super-admin/organizations?${queryParams.toString()}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error fetching organizations:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch organizations' },
    };
  }
};

/**
 * Get organization by ID
 */
export const getOrganizationById = async (
  organizationId: number
): Promise<{
  success: boolean;
  data?: OrganizationDetail;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<OrganizationDetail>>(
      `/super-admin/organizations/${organizationId}`
    );

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error fetching organization:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch organization' },
    };
  }
};

/**
 * Create a new organization
 */
export const createOrganization = async (
  organizationData: CreateOrganizationDTO
): Promise<{
  success: boolean;
  data?: { id: number };
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ id: number }>>(
      '/super-admin/organizations',
      organizationData
    );

    return {
      success: response.success,
      data: response.data,
      message: response.message || 'Organization created successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error creating organization:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create organization' },
    };
  }
};

/**
 * Update organization
 */
export const updateOrganization = async (
  organizationId: number,
  organizationData: UpdateOrganizationDTO
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<ApiResponse<null>>(
      `/super-admin/organizations/${organizationId}`,
      organizationData
    );

    return {
      success: response.success,
      message: response.message || 'Organization updated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error updating organization:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update organization' },
    };
  }
};

/**
 * Delete organization
 */
export const deleteOrganization = async (
  organizationId: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(
      `/super-admin/organizations/${organizationId}`
    );

    return {
      success: response.success,
      message: response.message || 'Organization deleted successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error deleting organization:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to delete organization' },
    };
  }
};

/**
 * Toggle organization status
 */
export const toggleOrganizationStatus = async (
  organizationId: number,
  isActive: boolean
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<null>>(
      `/super-admin/organizations/${organizationId}/toggle-status`,
      { isActive }
    );

    return {
      success: response.success,
      message: response.message || 'Organization status updated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error toggling organization status:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to toggle organization status' },
    };
  }
};

/**
 * Get users in an organization
 */
export const getOrganizationUsers = async (
  organizationId: number,
  params: {
    page?: number;
    limit?: number;
    search?: string;
  } = {}
): Promise<{
  success: boolean;
  data?: OrganizationUser[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params.page || 1));
    queryParams.append('limit', String(params.limit || 10));
    if (params.search) {
      queryParams.append('search', params.search);
    }

    const response = await httpClient.get<ApiResponse<OrganizationUser[]>>(
      `/super-admin/organizations/${organizationId}/users?${queryParams.toString()}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error fetching organization users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch organization users' },
    };
  }
};

/**
 * Get all active users (for assigning to organizations)
 * Returns users with is_deleted=0 and status=active
 * Excludes Super Admin users
 */
export const getAllActiveUsers = async (): Promise<{
  success: boolean;
  data?: ActiveUser[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<ActiveUser[]>>(
      '/super-admin/organizations/users/active'
    );

    return {
      success: true,
      data: response.data || [],
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error fetching active users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch active users' },
    };
  }
};

/**
 * Assign user to organization
 */
export const assignUserToOrganization = async (
  userId: string,
  organizationId: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<null>>(
      `/super-admin/organizations/users/${userId}/organizations/${organizationId}`
    );

    return {
      success: response.success,
      message: response.message || 'User assigned to organization successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error assigning user to organization:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to assign user to organization' },
    };
  }
};

/**
 * Bulk assign users to organization
 */
export const bulkAssignUsersToOrganization = async (
  userIds: string[],
  organizationId: number
): Promise<{
  success: boolean;
  message?: string;
  data?: {
    successCount: number;
    failedCount: number;
    skippedCount: number;
    details: {
      success: string[];
      failed: Array<{ userId: string; reason: string }>;
      skipped: Array<{ userId: string; reason: string }>;
    };
  };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<
      ApiResponse<{
        successCount: number;
        failedCount: number;
        skippedCount: number;
        details: {
          success: string[];
          failed: Array<{ userId: string; reason: string }>;
          skipped: Array<{ userId: string; reason: string }>;
        };
      }>
    >(`/super-admin/organizations/${organizationId}/users/bulk-assign`, {
      userIds,
    });

    return {
      success: response.success,
      message: response.message || 'Users assigned to organization successfully',
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error bulk assigning users to organization:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to assign users to organization' },
    };
  }
};

/**
 * Remove user from organization
 */
export const removeUserFromOrganization = async (
  userId: string,
  organizationId: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(
      `/super-admin/organizations/users/${userId}/organizations/${organizationId}`
    );

    return {
      success: response.success,
      message: response.message || 'User removed from organization successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error removing user from organization:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to remove user from organization' },
    };
  }
};

/**
 * Create a new platform user and immediately assign them to an organization.
 * Backed by `POST /super-admin/organizations/:organizationId/users/create`.
 *
 * Used by the SuperAdmin "Add Users to Organization → Create New User" tab so
 * a new hire can be onboarded straight into the right org without first
 * having to create them on the User Management page. Backend enforces:
 *   - email must be unique
 *   - role ∈ { learner | instructor | admin } (no super-admin)
 *   - manager (if provided) must already exist in the platform
 *
 * On success the user is created, profile rows written, manager link set
 * (`users.reports_to_uuid`), and an invitation email with a temp password
 * is sent best-effort.
 */
export interface CreateUserInOrganizationPayload {
  firstName: string;
  lastName: string;
  email: string;
  role: 'learner' | 'instructor' | 'admin';
  managerEmail?: string;
  department?: string;
  jobTitle?: string;
  location?: string;
  phone?: string;
}

export interface CreateUserInOrganizationResult {
  success: boolean;
  message?: string;
  data?: {
    userId: string;
    email: string;
    role: string;
    roleId: number;
    organizationId: number;
    organizationName: string;
    managerLinked: boolean;
    invitationSent: boolean;
    invitationError: string | null;
  };
  // Field-level validation errors from the backend (e.g., manager not found).
  errors?: Array<{ field: string; message: string }>;
  error?: { message: string } | null;
}

export const createUserInOrganization = async (
  organizationId: number,
  payload: CreateUserInOrganizationPayload
): Promise<CreateUserInOrganizationResult> => {
  try {
    const response = await httpClient.post<
      ApiResponse<CreateUserInOrganizationResult['data']> & {
        errors?: Array<{ field: string; message: string }>;
      }
    >(`/super-admin/organizations/${organizationId}/users/create`, payload);

    return {
      success: response.success,
      message: response.message,
      data: response.data,
      errors: response.errors,
      error: null,
    };
  } catch (error: any) {
    // httpClient throws on non-2xx; surface field-level errors when present.
    const responseData = error?.response?.data;
    return {
      success: false,
      message: responseData?.message ?? error?.message ?? 'Failed to create user',
      errors: responseData?.errors,
      error: { message: responseData?.message ?? error?.message ?? 'Failed to create user' },
    };
  }
};

/**
 * Get user's organizations
 */
export const getUserOrganizations = async (
  userId: string
): Promise<{
  success: boolean;
  data?: Organization[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Organization[]>>(
      `/super-admin/organizations/users/${userId}/organizations`
    );

    return {
      success: true,
      data: response.data || [],
      error: null,
    };
  } catch (error: any) {
    console.error('OrganizationService: Error fetching user organizations:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch user organizations' },
    };
  }
};
